import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LinkIcon from "@mui/icons-material/Link";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const tc = {
  text: "#172b4d",
  muted: "#5e6c84",
  border: "#dfe1e6",
  fieldBg: "#f4f5f7",
  primary: "#0c66e4",
  primaryHover: "#0055cc",
  inputText: "#000000",
};

export default function DescriptionRichEditor({ value, onCommit, resetKey }) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  const [aaAnchor, setAaAnchor] = useState(null);
  const [listAnchor, setListAnchor] = useState(null);
  const taRef = useRef(null);

  useEffect(() => {
    setDraft(value);
  }, [value, resetKey]);

  const wrap = useCallback((before, after = before) => {
    const el = taRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selLen = end - start;
    setDraft((d) => {
      const selected = d.slice(start, end);
      return d.slice(0, start) + before + selected + after + d.slice(end);
    });
    const pos = start + before.length + selLen + after.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  }, []);

  const insertAtCursor = useCallback((snippet) => {
    const el = taRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    setDraft((d) => d.slice(0, start) + snippet + d.slice(end));
    const pos = start + snippet.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  }, []);

  const prefixLines = useCallback((prefix) => {
    const el = taRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    let nextBlock = "";
    setDraft((d) => {
      const block = d.slice(start, end);
      const lines = block.split("\n");
      nextBlock = lines.map((line) => (line.startsWith(prefix) ? line : prefix + line)).join("\n");
      return d.slice(0, start) + nextBlock + d.slice(end);
    });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start, start + nextBlock.length);
    });
  }, []);

  const handleSave = () => {
    onCommit(draft.trimEnd());
  };

  const handleCancel = () => {
    setDraft(value);
  };

  const openFormattingHelp = () => {
    window.open("https://www.markdownguide.org/basic-syntax/", "_blank", "noopener,noreferrer");
  };

  return (
    <Box sx={{ mb: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
        <ViewHeadlineIcon sx={{ fontSize: 22, color: tc.muted }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: tc.text, fontSize: "1rem" }}>
          Description
        </Typography>
      </Stack>

      <Box
        sx={{
          border: focused ? `2px solid ${tc.primary}` : `1px solid ${tc.border}`,
          borderRadius: "3px",
          overflow: "hidden",
          bgcolor: tc.fieldBg,
          transition: "border-color 120ms ease",
        }}
      >
        {/* Toolbar */}
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          spacing={0.25}
          sx={{
            px: 0.75,
            py: 0.5,
            borderBottom: `1px solid ${tc.border}`,
            bgcolor: "#fafbfc",
            gap: 0.25,
          }}
        >
          <Tooltip title="Text style">
            <IconButton
              size="small"
              onClick={(e) => setAaAnchor(e.currentTarget)}
              sx={{ color: tc.text, borderRadius: 1, "&:hover": { bgcolor: "rgba(9,30,66,0.08)" } }}
            >
              <FormatSizeIcon sx={{ fontSize: 20 }} />
              <ArrowDropDownIcon sx={{ fontSize: 18, ml: -0.5 }} />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={aaAnchor} open={Boolean(aaAnchor)} onClose={() => setAaAnchor(null)}>
            <MenuItem
              onClick={() => {
                insertAtCursor("\n# ");
                setAaAnchor(null);
              }}
            >
              Heading 1
            </MenuItem>
            <MenuItem
              onClick={() => {
                insertAtCursor("\n## ");
                setAaAnchor(null);
              }}
            >
              Heading 2
            </MenuItem>
            <MenuItem
              onClick={() => {
                insertAtCursor("\n");
                setAaAnchor(null);
              }}
            >
              Normal text
            </MenuItem>
          </Menu>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: tc.border }} />

          <Tooltip title="Bold (Markdown)">
            <IconButton size="small" onClick={() => wrap("**", "**")} sx={{ color: tc.text }}>
              <FormatBoldIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic (Markdown)">
            <IconButton size="small" onClick={() => wrap("*", "*")} sx={{ color: tc.text }}>
              <FormatItalicIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="More">
            <IconButton size="small" sx={{ color: tc.text }}>
              <MoreHorizIcon />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: tc.border }} />

          <Tooltip title="List">
            <IconButton size="small" onClick={(e) => setListAnchor(e.currentTarget)} sx={{ color: tc.text }}>
              <FormatListBulletedIcon sx={{ fontSize: 20 }} />
              <ArrowDropDownIcon sx={{ fontSize: 18, ml: -0.5 }} />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={listAnchor} open={Boolean(listAnchor)} onClose={() => setListAnchor(null)}>
            <MenuItem
              onClick={() => {
                prefixLines("- ");
                setListAnchor(null);
              }}
            >
              Bullet list
            </MenuItem>
            <MenuItem
              onClick={() => {
                prefixLines("1. ");
                setListAnchor(null);
              }}
            >
              Numbered list
            </MenuItem>
          </Menu>

          <Tooltip title="Insert link">
            <IconButton size="small" onClick={() => wrap("[", "](https://)")} sx={{ color: tc.text }}>
              <LinkIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert image (Markdown)">
            <IconButton
              size="small"
              onClick={() => insertAtCursor("\n![alt text](https://)\n")}
              sx={{ color: tc.text }}
            >
              <ImageOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert">
            <IconButton size="small" sx={{ color: tc.text }}>
              <AddIcon sx={{ fontSize: 20 }} />
              <ArrowDropDownIcon sx={{ fontSize: 18, ml: -0.5 }} />
            </IconButton>
          </Tooltip>

          <Box sx={{ flex: 1 }} />

          <Tooltip title="Attachment">
            <IconButton size="small" sx={{ color: tc.text }}>
              <AttachFileIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Markdown">
            <Box
              component="span"
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: tc.muted,
                px: 0.75,
                py: 0.25,
                border: `1px solid ${tc.border}`,
                borderRadius: 1,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                height: 28,
              }}
            >
              M↓
            </Box>
          </Tooltip>
          <Tooltip title="Help">
            <IconButton size="small" onClick={openFormattingHelp} sx={{ color: tc.text }}>
              <HelpOutlineIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="😄 Say it with an emoji, just type ':'"
          spellCheck
          style={{
            width: "100%",
            minHeight: 180,
            padding: "12px 14px",
            border: "none",
            outline: "none",
            resize: "vertical",
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", sans-serif',
            fontSize: 14,
            lineHeight: 1.5,
            color: tc.inputText,
            backgroundColor: "#fff",
            boxSizing: "border-box",
          }}
        />
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              textTransform: "none",
              bgcolor: tc.primary,
              boxShadow: "none",
              px: 2,
              py: 0.75,
              fontWeight: 600,
              "&:hover": { bgcolor: tc.primaryHover, boxShadow: "none" },
            }}
          >
            Save
          </Button>
          <Button
            onClick={handleCancel}
            sx={{ textTransform: "none", color: tc.muted, fontWeight: 400, minWidth: 0, p: 0.5 }}
          >
            Cancel
          </Button>
        </Stack>
        <Button
          onClick={openFormattingHelp}
          sx={{
            textTransform: "none",
            color: tc.muted,
            fontSize: "0.8125rem",
            fontWeight: 400,
            bgcolor: tc.fieldBg,
            border: `1px solid ${tc.border}`,
            px: 1.5,
            py: 0.5,
            "&:hover": { bgcolor: "#ebeef2", borderColor: tc.border },
          }}
        >
          Formatting help
        </Button>
      </Stack>
    </Box>
  );
}
